import { User } from '../types';
import { getSupabase } from './supabaseClient';
import { debugLog } from '../utils/debugLogger';

export const FREE_STORY_LIMIT = 3;
export const PRO_STORY_LIMIT = 25;

export const authService = {
  async signup(name: string, email: string, password: string): Promise<User> {
    const { data, error } = await getSupabase().auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
        },
      },
    });

    if (error) {
      if (error.message.includes('rate limit')) {
        throw new Error("The email server is busy. Please try again in an hour.");
      }
      throw error;
    }
    
    if (!data.user) throw new Error("Signup failed");

    return this._mapSupabaseUserToAppUser(data.user);
  },

  async login(email: string, password: string): Promise<User> {
    const { data, error } = await getSupabase().auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    if (!data.user) throw new Error("Login failed");

    return this._mapSupabaseUserToAppUser(data.user);
  },

  async logout() {
    debugLog("AUTH", "authService: Logout initiated");
    try {
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("Logout timeout")), 3000));
      await Promise.race([getSupabase().auth.signOut(), timeout]);
      debugLog("AUTH", "authService: Supabase signOut complete");
    } catch (e: any) {
      debugLog("AUTH", "authService: Logout warning", e.message);
    } finally {
      // Robust cleanup of ALL potential Supabase-related keys to prevent stale state hangs
      for (const key in localStorage) {
        if (key.includes('supabase.auth.token') || key.startsWith('sb-')) {
          localStorage.removeItem(key);
        }
      }
    }
  },

  async getCurrentUser(): Promise<User | null> {
    // 4-second race to ensure startup doesn't hang if provider is unreachable or SDK is stuck
    const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000));
    
    const getUserPromise = (async () => {
      try {
        const sb = getSupabase();
        debugLog("AUTH", "getCurrentUser: Fetching session");
        const { data: { session }, error: sessionError } = await sb.auth.getSession();
        
        if (sessionError) {
          debugLog("AUTH", "getCurrentUser: Session fetch failed", sessionError);
          return null;
        }

        if (!session) {
          debugLog("AUTH", "getCurrentUser: No session found");
          return null;
        }

        // Use refreshSession to ensure we get the latest metadata (like email_confirmed_at)
        const { data: { user }, error } = await sb.auth.getUser();
        
        if (error) {
          debugLog("AUTH", "getCurrentUser: User check failed", error.message);
          if (error.status === 400 || error.message.toLowerCase().includes('refresh_token')) {
            await this.logout();
          }
          return null;
        }

        if (!user) return null;

        return this._mapSupabaseUserToAppUser(user);
      } catch (e: any) {
        debugLog("AUTH", "getCurrentUser: Critical failure", e.message);
        return null;
      }
    })();

    return Promise.race([getUserPromise, timeoutPromise]);
  },

  _mapSupabaseUserToAppUser(sbUser: any): User {
    const today = new Date().toISOString().split('T')[0];
    const lastDate = sbUser.user_metadata?.last_story_date;
    const dbCount = sbUser.user_metadata?.daily_story_count || 0;
    const isPro = sbUser.user_metadata?.is_pro || false;

    // Pro users: reset count daily. Free users: count is cumulative (3 total ever)
    const effectiveCount = (isPro && lastDate !== today) ? 0 : dbCount;

    return {
      id: sbUser.id,
      name: sbUser.user_metadata?.full_name || 'Parent',
      email: sbUser.email!,
      isPro,
      isVerified: !!sbUser.email_confirmed_at, // CRITICAL: This determines if they can proceed
      dailyStoryCount: effectiveCount,
      lastStoryDate: lastDate || today
    };
  },

  canGenerateStory(user: User): boolean {
    const today = new Date().toISOString().split('T')[0];

    // Pro users: new day means they can generate (count resets)
    // Free users: no daily reset, just check against lifetime limit
    if (user.isPro && user.lastStoryDate !== today) return true;

    const limit = user.isPro ? PRO_STORY_LIMIT : FREE_STORY_LIMIT;
    return user.dailyStoryCount < limit;
  },

  async incrementUsage(user: User): Promise<User> {
    const today = new Date().toISOString().split('T')[0];
    const isNewDay = user.lastStoryDate !== today;

    // Pro users: reset to 1 on new day. Free users: always increment (cumulative)
    const newCount = (user.isPro && isNewDay) ? 1 : user.dailyStoryCount + 1;

    const { data, error } = await getSupabase().auth.updateUser({
      data: {
        daily_story_count: newCount,
        last_story_date: today
      }
    });

    if (error) console.error("Failed to sync usage to cloud", error);

    return { ...user, dailyStoryCount: newCount, lastStoryDate: today };
  },

  async setProStatus(userId: string, isPro: boolean): Promise<void> {
    const { error } = await getSupabase().auth.updateUser({
      data: { is_pro: isPro }
    });
    if (error) {
        console.error("AuthService: Failed to sync is_pro status", error);
        throw error;
    }
  },

  async upgradeToPro(userId: string): Promise<void> {
    return this.setProStatus(userId, true);
  },

  async resendCode(email: string): Promise<void> {
    const { error } = await getSupabase().auth.resend({
      type: 'signup',
      email: email,
    });
    if (error) {
      if (error.message.includes('rate limit')) {
        throw new Error("You can only request a new link every 60 seconds.");
      }
      throw error;
    }
  }
};